import _ from "lodash";
import { CollectionAdapter } from "../../main/adapters/CollectionAdapter/CollectionAdapter";
import { HttpAdapter } from "../../main/adapters/HttpAdapter";
import { Fields } from "../../utils/fields/fields";

const InsightRules = new Mongo.Collection("insightRules", {
    connection: null
});

export class InsightRule extends CollectionAdapter {
    HttpAdapter = new HttpAdapter();
    baseUrlInsightRule = `${publicSettings.aws.baseUrls.dataAnalizer}/insight-rule`;

    constructor({ PopulateManager } = {}) {
        super(InsightRules);
        this.PopulateManager = PopulateManager;
    }

    async populate(selector, options = {}) {
        const response = await this.findCloud(selector, options);
        this.deleteMany(selector);
        const hasResponse = _.get(response, "list.length");
        if (hasResponse) this.createMany(response.list);
        return hasResponse ? true : false;
    }

    async getByIds(ids) {
        if (!ids.length) return;

        const response = await this.HttpAdapter.call(
            `${this.baseUrlInsightRule}/get-by-ids`,
            { ids }
        );

        return response || [];
    }

    async populateAccountingMonitoringBySource({
        source,
        type,
        isAnalysisPlus,
        matrix = false
    }) {
        const managerAccountingInsights = this.PopulateManager.findOneCollection(
            {
                collections: { $eq: "insightsRules" }
            }
        );

        const sourceWithType = `EMPRESA_NUCONT-${source}-${type}`;

        const keys = _.get(managerAccountingInsights, "keys") || [];

        try {
            if (!keys.includes(sourceWithType)) {
                const { rules, seriesIds } = await this.getMonitoringBySource({
                    companyId: "EMPRESA_NUCONT",
                    source,
                    type,
                    isAnalysisPlus,
                    matrix
                });

                rules.forEach((rule) => {
                    rule.name_sort = rule.name
                        ?.replace(/[^a-zA-Z\s]/g, "")
                        ?.trim();
                    this.create(rule);
                });
            }
        } catch (error) {
            console.error(error);
        }
    }

    async populateCompanyMonitoringBySource({
        companyId,
        source,
        type,
        isAnalysisPlus = false
    }) {
        const managerInishgts = this.PopulateManager.findOneCollection({
            collections: { $eq: "insightsRules" }
        });

        const sourceWithType = `${companyId}-${source}-${type}`;

        const keys = _.get(managerInishgts, "keys") || [];

        try {
            if (!keys.includes(sourceWithType)) {
                const { rules, seriesIds } = await this.getMonitoringBySource({
                    companyId,
                    source,
                    type,
                    isAnalysisPlus
                });

                const total = rules.length;

                for (let i = 0; i < total; i++) {
                    const rule = rules[i];
                    this.create(rule);
                }

                this.PopulateManager.modify(
                    { collections: { $eq: "insightsRules" } },
                    {
                        $addToSet: { keys: sourceWithType }
                    }
                );

                this.PopulateManager.create({
                    collections: "serieIds",
                    key: sourceWithType,
                    seriesIds
                });
                return seriesIds;
            } else {
                // const sourceWithType = `${source}-${type}`;

                const managerSerieIds = this.PopulateManager.findOneCollection({
                    collections: { $eq: "serieIds" },
                    key: { $eq: sourceWithType }
                });

                return managerSerieIds?.seriesIds || [];
            }
        } catch (error) {
            console.error(error);
        }
    }

    /**
     * @public
     * Busca insights por source e companyId
     * @param {Object} param0
     * @returns
     */
    async getMonitoringBySource({
        companyId,
        source,
        type,
        isAnalysisPlus = false,
        matrix = false
    }) {
        try {
            const input = {
                companyId,
                sources: [source],
                types: [type]
            };

            if (isAnalysisPlus) {
                input.options = {
                    analysisPlus: "included"
                };
            }

            if (matrix && type === "health") {
                if (!input?.options) {
                    input.options = {
                        matrix: true
                    };
                } else {
                    input.options.matrix = true;
                }
            }

            const response = await this.HttpAdapter.call(
                `${this.baseUrlInsightRule}/get-by-types`,
                input
            );

            return response;
        } catch (err) {
            console.error("err", err);
            return {};
        }
    }

    async updateGoals(goals) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrlInsightRule}/update-goals`,
            { goals }
        );

        return response;
    }

    /**
     * @public
     * Retorna insights agrupados
     */
    getInsightRulesGrouped({ companyId, type, sources, childrens, status }) {
        const selector = {
            sources,
            status
        };

        if (status === undefined) delete selector.status;

        if (type) selector.type = type;

        let allInsightRules = this.getLocalCompanyRules(companyId, selector, {
            fields: Fields.InsightRulesGroupd,
            sort: { position: 1 }
        });

        return this.groupRulesByGroup(
            allInsightRules.map((i) => ({ ...i, accordionOpened: false })),
            childrens
        );
    }

    groupRulesByGroup(rules, childrens) {
        let insightRules = [];

        const groupsNames = this.getGroups(rules);

        const aggroupedRules = _.groupBy(rules, "group");

        const orderedRules = {};

        groupsNames.forEach((group) => {
            orderedRules[group] = aggroupedRules[group];
        });

        if (childrens) return orderedRules;

        _.each(orderedRules, (rules, key) => {
            const sortedRules = _.sortBy(rules, "position");
            insightRules.push({ name: key, main: true });
            insightRules = insightRules.concat(sortedRules);
        });

        return insightRules;
    }

    /**
     * @public
     * @param {Array} rules
     * Retorna os grupos de acordo com as regras
     *
     * @returns {Array} retona um array de strings contendo nome dos grupos
     */
    getGroups(rules = []) {
        const groupsNames = [];

        rules.forEach((rule) => {
            if (groupsNames.indexOf(rule.group) === -1) {
                groupsNames.push(rule.group);
            }
        });

        return groupsNames;
    }

    async findCloud(selector, options = {}) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrlInsightRule}/get-by-types`,
            {
                ...selector,

                options: options || {}
            }
        );

        return response;
    }

    async hasCustomInsightsRulesFromCloud(companyId, sources, types) {
        const response = await this.findCloud({
            companyId,
            sources,
            types
        });

        return !!response.rules.length;
    }

    /**
     * @public
     *
     * @param {*} companyId
     * @param {*} query
     * @param {*} options
     * @returns
     */
    async getLocalCompanyRulesFromCloud(companyId, query, options) {
        let responseInsights = [];
        const types = ["audit", "health"];
        const hasCustomInsightsRulesFromCloud = await this.hasCustomInsightsRulesFromCloud(
            companyId,
            [query.sources],
            types
        );

        if (hasCustomInsightsRulesFromCloud) {
            responseInsights = await this.findCloud(
                {
                    companyId,
                    ...query,
                    sources: [query.sources],
                    types
                },
                options
            );
        } else {
            responseInsights = await this.findCloud(
                {
                    companyId: "EMPRESA_NUCONT",
                    ...query,
                    sources: [query.sources],
                    types
                },
                options
            );
        }

        return responseInsights.rules;
    }

    /**
     * @public
     *
     * @param {*} companyId
     * @param {*} query
     * @param {*} options
     * @returns
     */
    getLocalCompanyRules(companyId, query, options) {
        let allInsightRules = [];

        const hasCustomInsightsRules = this.hasCustomInsightsRules(
            companyId,
            query.sources
        );

        if (hasCustomInsightsRules) {
            allInsightRules = this.findCollection(
                {
                    companyId: { $eq: companyId },
                    ...query
                },
                options,
                "array"
            );
        } else {
            allInsightRules = this.findCollection(
                {
                    companyId: { $eq: "EMPRESA_NUCONT" },
                    ...query
                },
                options,
                "array"
            );
        }

        return allInsightRules;
    }

    hasCustomInsightsRules(companyId, sources) {
        return Boolean(
            this.findOneCollection({
                companyId: { $eq: companyId },
                sources: { $eq: sources }
            })
        );
    }

    async insertOne(insightRule, companyId, accountingId) {
        const response = await this.HttpAdapter.call(
            `${this.baseUrlAnalyzer}/insight-rule-create`,
            {
                insightRule,
                companyId,
                accountingId
            }
        );
        const rules = response.rules;

        const hasClonedRules = rules.length > 1 ? true : false;

        if (hasClonedRules) this.deleteMany({});
        this.createMany(rules);
    }

    async updateMany(
        companyId,
        accountingId,
        modifiedRules,
        source = "accounting"
    ) {
        const input = {
            companyId,
            accountingId,
            modifiedRules: modifiedRules.map((rule) => ({
                ...rule,
                options: [].concat(rule.options)
            })),
            source
        };

        await this.HttpAdapter.call(`${this.baseUrlInsightRule}/save`, input);

        const response = await this.getByIds(modifiedRules.map((r) => r._id));
        const rules = response.rules;

        // temporario, buscar Nº de regras padrão nucont
        const hasClonedRules = rules.length === 48 ? true : false;

        if (hasClonedRules) {
            this.deleteMany({});
            this.createMany(rules);
        } else {
            rules.forEach((rule) => {
                this.delete(rule._id);
                this.create(rule);
            });
        }
    }
}
